import { isNonEmpty } from '@sgme/fp';
import type { TradeRequestPayload } from 'epic/common-form/Trade';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type {
  AddonManagementQuoteRequestPayload,
  AddonQuoteRequestPayload,
} from 'models/quote/AddonQuoteRequestPayload';
import type { QuoteWarning } from 'models/QuoteWarning';
import { createSelector } from 'reselect';
import type { RemunerationMode, SolveMode } from 'state/addonForm';
import { getFormEnrichedData } from 'state/formData/formData.selectors';
import * as fromQuoteResult from 'state/quotes/results/results.selectors';
import {
  getAddonManagementFormQuoteResult,
  getAddonManagementFormTradeResult,
} from 'state/selectors/common/addonManagementForm';
import type { AppState } from 'state/state.model';
import {
  getUserPreferencesLanguage,
  getUserPreferencesUnderlyingCodeType,
} from 'state/userPreferences/userPreferences.selectors';
import { roundNumberToPrecision } from 'utils/core/math';

import { getAddonManagementFormQuoteDetails } from './quoteDetails/quoteDetails.selectors';

export * from './infos/infos.selectors';
export * from './metaData/fields/fields.selectors';

const getForm = (state: AppState, formId: string) => state.addonManagementForm?.infos[formId];
const getAllMetadata = (state: AppState) => state.addonManagementForm?.metaData.fields;

export const getAddonManagementFormFieldIds = createSelector(
  (state: AppState) => state.addonManagementForm?.metaData.fields,
  fields => Object.keys(fields),
);

export function getAddonManagementFormLastQuoteId(state: AppState, formId: string) {
  return getForm(state, formId)?.lastQuoteId;
}

export function getAddonManagementFormIdByLastQuoteId(state: AppState, quoteId: string): string | undefined {
  const infosState = state.addonManagementForm?.infos;
  const infoStateKeys = Object.keys(infosState) ?? [];
  return infoStateKeys?.find(key => infosState[key].lastQuoteId === quoteId);
}

export function getAddonManagementQuoteResult(state: AppState, formId: string) {
  const lastQuoteId = getAddonManagementFormLastQuoteId(state, formId);
  return lastQuoteId ? fromQuoteResult.getQuoteResult(state.quotes.results, lastQuoteId) : undefined;
}

export function getRequestForQuotes(state: AppState, formIds: readonly string[]): AddonManagementQuoteRequestPayload {
  return {
    quotes: formIds.map(formId => getRequestForQuote(state, formId)),
  };
}
export function getRequestForQuote(state: AppState, formId: string): AddonQuoteRequestPayload {
  const formEnrichedData = getFormEnrichedData(state, formId);
  const lastQuoteId = getForm(state, formId).lastQuoteId;
  const notionalAmount = formEnrichedData['NotionalAmount'] as number;
  const SelectedOnBehalfClient = formEnrichedData['SelectedOnBehalfClient'] as ISalesCounterpart;

  const remunerationMode = formEnrichedData['RemunerationMode'] as RemunerationMode;
  const requestedPrice = formEnrichedData[getRequestedPriceKey(remunerationMode)] as number;
  return {
    quoteId: lastQuoteId,
    originalQuoteId: formEnrichedData['QuoteId'] as string,
    productId: formId,
    tradeSide: 'Sell',
    notional: notionalAmount,
    notionalInUnits: undefined,
    notionalType: 'Currency',
    bdrId: SelectedOnBehalfClient.id,
    requestPrice: requestedPrice,
    dealSplits: [
      {
        Custodian: formEnrichedData['DealSplit'] as string,
        NotionalAmountInGrid: notionalAmount,
      },
    ],
    remunerationMode: remunerationMode,
    solveFor: formEnrichedData['SolvingMode'] as SolveMode,
    xMargin: formEnrichedData['XMargin'] as number,
    mergeTradeRequested: false,
    mergeTradeId: undefined,
    wrapper: 'Note',
    primaryDealId: undefined,
    instrumentContractName: formEnrichedData['ProductSubtype'] as string,
  };
}

function getRequestedPriceKey(remunerationMode: 'Upfront' | 'Reoffer' | 'Premium') {
  switch (remunerationMode) {
    case 'Upfront':
      return 'UpfrontFee';
    case 'Reoffer':
      return 'OfferPrice';
    default:
      return 'FixedAmountInPercent';
  }
}

export function getAddonManagementTradeRequest(state: AppState, formId: string) {
  const lastQuoteId = getAddonManagementFormLastQuoteId(state, formId);
  const quoteResult = getAddonManagementQuoteResult(state, formId);
  const defaultUnderlyingCodeType = getUserPreferencesUnderlyingCodeType(state);
  const languageCode = getUserPreferencesLanguage(state);
  const formEnrichedData = getFormEnrichedData(state, formId);
  const salesMargin = roundNumberToPrecision((formEnrichedData.XMargin as number) / 100, 6);
  const priceEnhancementSalesMargin = roundNumberToPrecision(quoteResult?.priceEnhancement?.xMargin ?? 0, 6);
  const tradingOffer = roundNumberToPrecision((formEnrichedData.TradingPrice as number) / 100, 6);
  const priceEnhancementTradingOffer = roundNumberToPrecision(quoteResult?.priceEnhancement?.tradingOffer ?? 0, 6);
  const priceAdjustmentUserOverride =
    tradingOffer !== priceEnhancementTradingOffer || salesMargin !== priceEnhancementSalesMargin
      ? { salesMargin: salesMargin, tradingOffer: tradingOffer }
      : null;
  const originalQuoteId = formEnrichedData['QuoteId'] as string;

  const tradeRequest: TradeRequestPayload = {
    externalQuoteId: quoteResult!.quoteExternalId,
    internalQuoteId: lastQuoteId,
    originalQuoteId,
    instrumentContractName: getAddonManagementFormQuoteDetails(state, formId)?.InstrumentContractName ?? undefined,
    wrapper: 'Note',
    screenMode: 'AddonManagement',
    side: undefined,
    issuePrice: quoteResult!.priceEnhancement!.issuePrice,
    priceAdjustmentUserOverride: priceAdjustmentUserOverride,
    sponsoFees: undefined,
    executionOnly: false,
    comment: '',
    defaultUnderlyingCodeType,
    languageCode,
    addonAction: 'Increase',
  };

  return tradeRequest;
}

export function getAddonManagementFormErrorsById(
  appState: AppState,
  formId: string,
): readonly NextWorkflowError[] | undefined {
  const quoteResult = getAddonManagementFormQuoteResult(appState, formId);
  if (!quoteResult) {
    return undefined;
  }
  const tradeResult = getAddonManagementFormTradeResult(appState, formId);
  const quoteResponseErrors = quoteResult.rejections;
  const tradeResponseErrors = tradeResult?.tradeRejections;
  return quoteResponseErrors ?? tradeResponseErrors;
}

export function getAddonManagementFormTechnicalErrorsById(
  appState: AppState,
  formId: string,
): readonly NextWorkflowError[] | undefined {
  const quoteResult = getAddonManagementFormQuoteResult(appState, formId);
  const quoteErrorId = quoteResult?.technicalError?.message;
  const isQuoteTimeout = quoteErrorId?.includes('Timeout');
  const quoteOriginMessage = isQuoteTimeout ? 'form.status.Timeout.QuoteTimeout' : quoteErrorId;
  if (quoteErrorId !== undefined) {
    return [
      {
        originMessage: quoteOriginMessage,
        rejectionType: isQuoteTimeout ? 'Timeout Error' : 'Technical Error',
      },
    ];
  }

  const tradeResult = getAddonManagementFormTradeResult(appState, formId);
  const tradeErrorId = tradeResult?.technicalError?.message;
  const isTradeTimeout = tradeErrorId?.includes('Timeout');
  const tradeOriginMessage = isTradeTimeout ? 'form.status.Timeout.TradeTimeout' : tradeErrorId;
  if (tradeErrorId !== undefined) {
    return [
      {
        originMessage: tradeOriginMessage,
        rejectionType: isTradeTimeout ? 'Timeout Error' : 'Technical Error',
      },
    ];
  }

  return undefined;
}

export function getHasTechnicalErrors(appState: AppState, formId: string): boolean {
  const technicalErrors = getAddonManagementFormTechnicalErrorsById(appState, formId);
  return isNonEmpty(technicalErrors) && technicalErrors[0].rejectionType === 'Technical Error';
}

export function getHasTimeoutErrors(appState: AppState, formId: string): boolean {
  const technicalErrors = getAddonManagementFormTechnicalErrorsById(appState, formId);
  return isNonEmpty(technicalErrors) && technicalErrors[0].rejectionType === 'Timeout Error';
}

export function getAddonManagementFormQuoteWarnings(appState: AppState, formId: string): QuoteWarning[] | undefined {
  const quoteResult = getAddonManagementFormQuoteResult(appState, formId);
  return quoteResult ? quoteResult?.warnings : undefined;
}

export function isColumnHiddenForAllForms(appState: AppState, columnId: string): boolean {
  // Check if the column is hidden for all forms
  const fieldsMetadata = getAllMetadata(appState);
  const values = Object.values(fieldsMetadata);

  return !values.some(x => !(x[columnId]?.isHidden ?? false));
}
